<template>
  <div class="st-page">
    <Navigation />
    <div class="container" style="padding-top:20px;">
      <div class="d-flex align-items-center justify-content-between mb-4">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <div><h4 class="st-page-title"><i class="mdi mdi-access-point me-2"></i>Tap Points</h4></div>
        </div>
        <button class="st-btn st-btn-blue" @click="openAdd"><i class="mdi mdi-plus me-1"></i>Add Tap Point</button>
      </div>

      <!-- Type Filter Tabs -->
      <div class="st-tabs">
        <button class="st-tab" :class="{active:filterType===''}" @click="filterType=''">All <span class="st-tab-count">{{ items.length }}</span></button>
        <button class="st-tab" :class="{active:filterType===t.value}" v-for="t in types" :key="t.value" @click="filterType=t.value">
          <span class="st-type-dot" :style="{background:t.color}"></span>{{ t.label }}
        </button>
      </div>

      <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
      <div v-else>
        <!-- Cards Grid -->
        <div class="st-grid" v-if="filteredItems.length">
          <div class="st-tp-card" v-for="tp in filteredItems" :key="tp.id">
            <div class="d-flex align-items-start justify-content-between mb-3">
              <div class="d-flex align-items-center gap-3">
                <div class="st-tp-icon" :style="{background: typeColor(tp.type) + '20', color: typeColor(tp.type)}">
                  <i class="mdi" :class="typeIcon(tp.type)"></i>
                </div>
                <div class="flex-fill" style="min-width:0;">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <div class="st-tp-name">{{ tp.name }}</div>
                    <span class="st-plate-badge" v-if="tp.bus_registration_number">{{ tp.bus_registration_number }}</span>
                  </div>
                  <span class="st-badge" :style="{background: typeColor(tp.type) + '22', color: typeColor(tp.type)}">{{ typeLabel(tp.type) }}</span>
                  <span class="st-route-label" v-if="tp.bus_route"> · {{ tp.bus_route }}</span>
                </div>
              </div>
              <div class="d-flex gap-1">
                <button class="st-btn st-btn-sm st-btn-blue" @click="openEdit(tp)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                <button class="st-btn st-btn-sm st-btn-red" @click="deleteTapPoint(tp)" title="Delete"><i class="mdi mdi-delete"></i></button>
              </div>
            </div>
            <div class="st-tp-details">
              <div class="st-tp-detail" v-if="tp.campus_name"><i class="mdi mdi-domain me-1"></i>{{ tp.campus_name }}</div>
              <div class="st-tp-detail" v-if="tp.bus_driver_name"><i class="mdi mdi-account me-1" style="color:#F59E0B;"></i>Driver: {{ tp.bus_driver_name }}<span v-if="tp.bus_driver_phone"> ({{ tp.bus_driver_phone }})</span></div>
              <div class="st-tp-detail">
                <i class="mdi me-1" :class="tp.is_gps_device == 1 ? 'mdi-crosshairs-gps' : 'mdi-crosshairs-off'" :style="{color: tp.is_gps_device == 1 ? '#10B981' : '#8BA4C4'}"></i>
                GPS {{ tp.is_gps_device == 1 ? 'Enabled' : 'Disabled' }}
              </div>
              <div class="st-tp-detail" v-if="tp.gps_lat && tp.gps_lng">
                <i class="mdi mdi-map-marker me-1" style="color:#2E86DE;"></i>
                <a :href="'https://maps.google.com/?q=' + tp.gps_lat + ',' + tp.gps_lng" target="_blank" class="st-gps-link">{{ parseFloat(tp.gps_lat).toFixed(4) }}, {{ parseFloat(tp.gps_lng).toFixed(4) }}</a>
              </div>
            </div>
            <div class="st-tp-status mt-2">
              <span class="st-badge" :class="tp.is_active == 1 ? 'st-badge-success' : 'st-badge-danger'">{{ tp.is_active == 1 ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>
        </div>
        <div v-else class="st-card st-empty"><i class="mdi mdi-access-point"></i>No tap points found. Add your first tap point to start tracking attendance.</div>
      </div>

      <!-- ADD / EDIT MODAL -->
      <div class="st-modal-overlay" v-if="showModal" @click.self="showModal=false">
        <div class="st-modal">
          <div class="st-modal-title">
            <span><i class="mdi me-2" :class="editMode ? 'mdi-pencil' : 'mdi-plus-circle'"></i>{{ editMode ? 'Edit Tap Point' : 'Add Tap Point' }}</span>
            <button class="st-modal-close" @click="showModal=false">&times;</button>
          </div>
          <div class="st-alert st-alert-danger" v-if="formError">{{ formError }}</div>

          <!-- Success State -->
          <div v-if="formSuccess" class="text-center">
            <div class="st-success-icon"><i class="mdi mdi-check-circle"></i></div>
            <h5 style="color:#E8EDF4;font-weight:700;margin:16px 0 8px;">{{ editMode ? 'Tap Point Updated!' : 'Tap Point Added!' }}</h5>
            <p style="color:#8BA4C4;font-size:14px;">{{ form.name }} — {{ typeLabel(form.type) }}</p>
            <div class="d-flex gap-2 mt-4">
              <button class="btn st-btn-primary flex-fill" @click="openAdd" v-if="!editMode"><i class="mdi mdi-plus me-1"></i>Add Another</button>
              <button class="btn flex-fill" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:12px 20px;" @click="showModal=false;formSuccess=false">Close</button>
            </div>
          </div>

          <!-- Form -->
          <div v-else>
            <div class="mb-3">
              <label class="st-label">Tap Point Name *</label>
              <input class="st-input" v-model="form.name" placeholder="e.g. Main Gate, Classroom Block A, School Bus 1" />
            </div>
            <div class="mb-3">
              <label class="st-label">Type *</label>
              <div class="st-type-grid">
                <div class="st-type-option" v-for="t in types" :key="t.value"
                     :class="{selected: form.type === t.value}"
                     @click="form.type = t.value">
                  <i class="mdi" :class="t.icon" :style="{color: t.color}"></i>
                  <div class="st-type-option-label">{{ t.label }}</div>
                  <div class="st-type-option-desc">{{ t.desc }}</div>
                </div>
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-6">
                <label class="st-label">Campus</label>
                <select class="st-select" v-model="form.campusId">
                  <option value="">None / All</option>
                  <option v-for="c in campuses" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="col-6" v-if="form.type === 'bus_boarding' || form.type === 'bus_dropoff'">
                <label class="st-label">Bus *</label>
                <select class="st-select" v-model="form.busId">
                  <option value="">Select bus...</option>
                  <option v-for="b in buses" :key="b.id" :value="b.id">{{ b.name }} — {{ b.registration_number || 'No plate' }}{{ b.route ? ' (' + b.route + ')' : '' }}</option>
                </select>
              </div>
            </div>
            <div class="mb-3">
              <label class="st-label d-flex align-items-center gap-2">
                <input type="checkbox" v-model="form.isGpsDevice" style="width:18px;height:18px;accent-color:#2E86DE;" />
                GPS-enabled device (live tracking, e.g. bus)
              </label>
              <small style="color:#8BA4C4;font-size:12px;">Enable for buses or mobile devices. Disable for fixed locations like gates and classrooms.</small>
            </div>
            <div class="mb-3" v-if="!form.isGpsDevice">
              <label class="st-label"><i class="mdi mdi-map-marker me-1"></i>Pin Location on Map *</label>
              <small style="color:#8BA4C4;font-size:12px;display:block;margin-bottom:8px;">Click on the map to set the location, or use the search box to find your school.</small>
              <div class="st-map-search-wrap">
                <i class="mdi mdi-magnify"></i>
                <input class="st-map-search" v-model="mapSearch" @keyup.enter="searchLocation" placeholder="Search location, e.g. St Patrick Iten..." />
                <button class="st-map-search-btn" @click="searchLocation"><i class="mdi mdi-arrow-right"></i></button>
              </div>
              <div :id="'tapPointMap-' + modalKey" class="st-tap-map"></div>
              <div class="st-coords-display" v-if="form.gpsLat && form.gpsLng">
                <i class="mdi mdi-crosshairs-gps me-1" style="color:#10B981;"></i>
                <span>{{ parseFloat(form.gpsLat).toFixed(6) }}, {{ parseFloat(form.gpsLng).toFixed(6) }}</span>
                <a :href="'https://maps.google.com/?q=' + form.gpsLat + ',' + form.gpsLng" target="_blank" class="st-gps-link ms-2"><i class="mdi mdi-open-in-new"></i> View</a>
              </div>
              <div class="st-coords-display" v-else style="color:#F59E0B;">
                <i class="mdi mdi-alert me-1"></i> Click on the map to pin the location
              </div>
            </div>
            <div class="d-flex gap-2">
              <button class="btn st-btn-primary flex-fill" @click="saveTapPoint" :disabled="processing">
                <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
                {{ processing ? 'Saving...' : (editMode ? 'Update Tap Point' : 'Add Tap Point') }}
              </button>
              <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:12px 20px;" @click="showModal=false">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: "TapPointManagement",
  components: { Navigation },
  data() {
    return {
      loading: true,
      items: [],
      campuses: [],
      buses: [],
      filterType: '',
      showModal: false,
      editMode: false,
      editId: null,
      processing: false,
      formError: '',
      formSuccess: false,
      form: { name: '', type: '', campusId: '', busId: '', isGpsDevice: false, gpsLat: '', gpsLng: '' },
      mapSearch: '',
      modalMap: null,
      modalMarker: null,
      modalKey: 0,
      types: [
        { value: 'gate_entry', label: 'Gate Entry', desc: 'Students arriving at school', icon: 'mdi-login', color: '#10B981' },
        { value: 'gate_exit', label: 'Gate Exit', desc: 'Students leaving school', icon: 'mdi-logout', color: '#EF4444' },
        { value: 'classroom', label: 'Classroom', desc: 'Classroom check-in', icon: 'mdi-google-classroom', color: '#2E86DE' },
        { value: 'pos_dining', label: 'POS Dining', desc: 'Dining hall / Canteen POS', icon: 'mdi-silverware-fork-knife', color: '#EC4899' },
        { value: 'pos_tuckshop', label: 'POS Tuckshop', desc: 'Tuckshop / Kiosk POS', icon: 'mdi-store', color: '#8B5CF6' },
        { value: 'bus_boarding', label: 'Bus Board', desc: 'Boarding the school bus', icon: 'mdi-bus', color: '#F59E0B' },
        { value: 'bus_dropoff', label: 'Bus Drop-off', desc: 'Dropped off from bus', icon: 'mdi-bus-stop', color: '#FB923C' },
        { value: 'event', label: 'Event', desc: 'Special event check-in', icon: 'mdi-calendar-star', color: '#A855F7' },
      ]
    };
  },
  computed: {
    filteredItems() {
      if (!this.filterType) return this.items;
      return this.items.filter(tp => tp.type === this.filterType);
    }
  },
  mounted() {
    this.loadTapPoints(); this.loadCampuses(); this.loadBuses(); this.loadLeaflet();
  },
  watch: {
    showModal(val) {
      if (val && !this.form.isGpsDevice) {
        this.$nextTick(() => { setTimeout(() => this.initModalMap(), 200); });
      }
    },
    'form.isGpsDevice'(val) {
      if (!val && this.showModal) {
        this.$nextTick(() => { setTimeout(() => this.initModalMap(), 200); });
      }
    }
  },
  methods: {
    loadLeaflet() {
      if (!document.getElementById('leaflet-css')) {
        var link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      if (!window.L) {
        var script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        document.head.appendChild(script);
      }
    },
    initModalMap() {
      if (!window.L) return;
      var mapId = 'tapPointMap-' + this.modalKey;
      var mapEl = document.getElementById(mapId);
      if (!mapEl) return;

      // Destroy old map if exists
      if (this.modalMap) { this.modalMap.remove(); this.modalMap = null; }

      var lat = this.form.gpsLat ? parseFloat(this.form.gpsLat) : 0.3633;
      var lng = this.form.gpsLng ? parseFloat(this.form.gpsLng) : 35.4878;
      var zoom = (this.form.gpsLat && this.form.gpsLng) ? 17 : 13;

      this.modalMap = L.map(mapId).setView([lat, lng], zoom);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 19
      }).addTo(this.modalMap);

      // Add marker if coordinates exist
      if (this.form.gpsLat && this.form.gpsLng) {
        this.modalMarker = L.marker([lat, lng], { draggable: true }).addTo(this.modalMap);
        this.modalMarker.on('dragend', (e) => {
          var pos = e.target.getLatLng();
          this.form.gpsLat = pos.lat.toFixed(8);
          this.form.gpsLng = pos.lng.toFixed(8);
        });
      }

      // Click to place/move marker
      this.modalMap.on('click', (e) => {
        this.form.gpsLat = e.latlng.lat.toFixed(8);
        this.form.gpsLng = e.latlng.lng.toFixed(8);
        if (this.modalMarker) {
          this.modalMarker.setLatLng(e.latlng);
        } else {
          this.modalMarker = L.marker(e.latlng, { draggable: true }).addTo(this.modalMap);
          this.modalMarker.on('dragend', (ev) => {
            var pos = ev.target.getLatLng();
            this.form.gpsLat = pos.lat.toFixed(8);
            this.form.gpsLng = pos.lng.toFixed(8);
          });
        }
      });
    },
    async searchLocation() {
      if (!this.mapSearch || this.mapSearch.length < 3) return;
      try {
        var resp = await fetch('https://nominatim.openstreetmap.org/search?format=json&q=' + encodeURIComponent(this.mapSearch) + '&limit=1');
        var data = await resp.json();
        if (data.length > 0) {
          var lat = parseFloat(data[0].lat);
          var lng = parseFloat(data[0].lon);
          this.form.gpsLat = lat.toFixed(8);
          this.form.gpsLng = lng.toFixed(8);
          if (this.modalMap) {
            this.modalMap.setView([lat, lng], 17);
            if (this.modalMarker) {
              this.modalMarker.setLatLng([lat, lng]);
            } else {
              this.modalMarker = L.marker([lat, lng], { draggable: true }).addTo(this.modalMap);
              this.modalMarker.on('dragend', (e) => {
                var pos = e.target.getLatLng();
                this.form.gpsLat = pos.lat.toFixed(8);
                this.form.gpsLng = pos.lng.toFixed(8);
              });
            }
          }
        } else {
          alert('Location not found. Try a more specific search.');
        }
      } catch (e) { alert('Search failed'); }
    },
    async loadTapPoints() {
      this.loading = true;
      try {
        var r = await executeJson({ function: 'getTapPoints' }, simitrack);
        if (r.data.success) this.items = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    async loadCampuses() {
      try {
        var r = await executeJson({ function: 'getCampuses' }, simitrack);
        if (r.data.success) this.campuses = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) {}
    },
    async loadBuses() {
      try {
        var r = await executeJson({ function: 'getBuses' }, simitrack);
        if (r.data.success) this.buses = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) {}
    },
    typeColor(type) {
      var found = this.types.find(t => t.value === type);
      return found ? found.color : '#8BA4C4';
    },
    typeLabel(type) {
      var found = this.types.find(t => t.value === type);
      return found ? found.label : type;
    },
    typeIcon(type) {
      var found = this.types.find(t => t.value === type);
      return found ? found.icon : 'mdi-access-point';
    },
    openAdd() {
      this.editMode = false; this.editId = null;
      this.formError = ''; this.formSuccess = false;
      this.mapSearch = ''; this.modalMarker = null; this.modalKey++;
      this.form = { name: '', type: '', campusId: '', busId: '', isGpsDevice: false, gpsLat: '', gpsLng: '' };
      this.showModal = true;
    },
    openEdit(tp) {
      this.editMode = true; this.editId = tp.id;
      this.formError = ''; this.formSuccess = false;
      this.mapSearch = ''; this.modalMarker = null; this.modalKey++;
      this.form = {
        name: tp.name || '',
        type: tp.type || '',
        campusId: tp.campus_id || '',
        busId: tp.bus_id || '',
        isGpsDevice: tp.is_gps_device == 1,
        gpsLat: tp.gps_lat || '',
        gpsLng: tp.gps_lng || ''
      };
      this.showModal = true;
    },
    async saveTapPoint() {
      this.formError = '';
      if (!this.form.name) { this.formError = 'Name is required'; return; }
      if (!this.form.type) { this.formError = 'Select a tap point type'; return; }
      this.processing = true;
      try {
        var params = {
          function: this.editMode ? 'updateTapPoint' : 'addTapPoint',
          name: this.form.name,
          type: this.form.type,
          campusId: this.form.campusId,
          busId: this.form.busId,
          isGpsDevice: this.form.isGpsDevice ? 1 : 0,
          gpsLat: this.form.gpsLat,
          gpsLng: this.form.gpsLng
        };
        if (this.editMode) params.tapPointId = this.editId;
        var r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.formSuccess = true;
          this.loadTapPoints();
        } else { this.formError = r.data.message || 'Failed'; }
      } catch (e) { this.formError = 'Connection error'; }
      this.processing = false;
    },
    async deleteTapPoint(tp) {
      if (!confirm('Delete tap point "' + tp.name + '"? This cannot be undone.')) return;
      try {
        var r = await executeJson({ function: 'deleteTapPoint', tapPointId: tp.id }, simitrack);
        if (r.data.success) this.loadTapPoints();
        else alert(r.data.message || 'Failed');
      } catch (e) { alert('Connection error'); }
    }
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-tabs{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:20px;}
.st-tab{background:#0F2347;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:50px;padding:7px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;display:flex;align-items:center;gap:6px;}
.st-tab.active,.st-tab:hover{background:rgba(46,134,222,.15);border-color:#2E86DE;color:#4FC3F7;}
.st-tab-count{background:rgba(46,134,222,.2);padding:1px 8px;border-radius:10px;font-size:11px;}
.st-type-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
.st-grid{display:grid;grid-template-columns:repeat(auto-fill, minmax(340px, 1fr));gap:16px;}
.st-tp-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:20px;transition:border .2s;}
.st-tp-card:hover{border-color:#2E86DE;}
.st-tp-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0;}
.st-tp-name{color:#E8EDF4;font-size:15px;font-weight:700;margin-bottom:4px;}
.st-plate-badge{background:rgba(245,158,11,.15);color:#F59E0B;padding:2px 10px;border-radius:6px;font-size:13px;font-weight:800;letter-spacing:1px;font-family:monospace;}
.st-route-label{color:#8BA4C4;font-size:12px;}
.st-tp-details{display:flex;flex-wrap:wrap;gap:12px;margin-top:8px;}
.st-tp-detail{color:#8BA4C4;font-size:12px;display:flex;align-items:center;}
.st-gps-link{color:#4FC3F7;text-decoration:none;font-size:12px;}.st-gps-link:hover{text-decoration:underline;}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;}
.st-btn{border:none;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-btn-sm{padding:5px 10px;font-size:12px;}
.st-btn-blue{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-btn-blue:hover{background:rgba(46,134,222,.25);}
.st-btn-red{background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.3);}
.st-btn-red:hover{background:rgba(239,68,68,.25);}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:10px;padding:12px;font-size:15px;}
.st-btn-primary:disabled{opacity:.6;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;}.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
.st-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:16px;padding:28px;width:100%;max-width:540px;max-height:90vh;overflow-y:auto;}
.st-modal-title{color:#E8EDF4;font-size:18px;font-weight:700;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;}
.st-modal-close{color:#8BA4C4;cursor:pointer;font-size:22px;background:none;border:none;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;transition:border .2s;}
.st-input:focus,.st-select:focus{border-color:#2E86DE;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:16px;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.st-success-icon{width:72px;height:72px;background:rgba(16,185,129,.12);border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto;}
.st-success-icon i{font-size:40px;color:#10B981;}

/* Type Selector Grid */
.st-type-grid{display:grid;grid-template-columns:repeat(3, 1fr);gap:8px;}
.st-type-option{background:#0A1628;border:2px solid #1A3A6A;border-radius:10px;padding:12px 10px;text-align:center;cursor:pointer;transition:all .2s;}
.st-type-option:hover{border-color:#2E86DE;}
.st-type-option.selected{border-color:#2E86DE;background:rgba(46,134,222,.08);}
.st-type-option i{font-size:24px;display:block;margin-bottom:4px;}
.st-type-option-label{color:#E8EDF4;font-size:12px;font-weight:700;}
.st-type-option-desc{color:#8BA4C4;font-size:10px;margin-top:2px;line-height:1.3;}

/* Map Picker */
.st-tap-map{width:100%;height:250px;border-radius:12px;overflow:hidden;border:1px solid #1A3A6A;margin-bottom:8px;}
.st-map-search-wrap{position:relative;margin-bottom:8px;display:flex;gap:4px;}
.st-map-search-wrap i{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8BA4C4;font-size:16px;z-index:1;}
.st-map-search{flex:1;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:8px 12px 8px 34px;color:#E8EDF4;font-size:13px;outline:none;}
.st-map-search:focus{border-color:#2E86DE;}
.st-map-search-btn{background:rgba(46,134,222,.15);border:1px solid rgba(46,134,222,.3);color:#4FC3F7;border-radius:10px;padding:8px 14px;cursor:pointer;font-size:14px;}
.st-map-search-btn:hover{background:rgba(46,134,222,.25);}
.st-coords-display{display:flex;align-items:center;color:#8BA4C4;font-size:12px;font-family:monospace;padding:6px 0;}
.st-gps-link{color:#4FC3F7;text-decoration:none;font-size:12px;}.st-gps-link:hover{text-decoration:underline;}

@media(max-width:768px){.st-type-grid{grid-template-columns:repeat(2, 1fr);}.st-grid{grid-template-columns:1fr;}}
</style>